import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { ChargingUserStatus } from 'src/app/shared/enums/charging-user-status.enum';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { ChargingUser, ChargingUserSearchEvent } from '../charging-user.model';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { PagedResult, PagingOptions } from 'src/app/shared/models/paging';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { ExpandListSort, SnackbarService } from 'ok-angular-shared/components';

enum ChargingUserListQueryParams {
  chargingUserId = 'chargingUserId',
  organizationId = 'organizationId',
  departmentId = 'departmentId',
  query = 'query',
  chargingUserStatus = 'chargingUserStatus',
  idTagUserType = 'idTagUserType',
}

export interface FilterState {
  organizationId: number | null;
  departmentId: number | null;
  query: string | null;
  selectedChargingUserStatus: ChargingUserStatus | null;
  selectedIdTagUserType: IdTagUserType | null;
  page: PageEvent;
  chargingUserId: number | null;
  sort: Sort | null;
}

@Injectable()
export class ChargingUserListStateService extends PagerBase {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private chargingUserService = inject(ChargingUserService);
  private filteringService = inject(FilteringService);
  private snackbarService = inject(SnackbarService);

  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);

  public loading = signal<boolean>(false);
  public currentSort = signal<ExpandListSort<ChargingUser>>({ column: 'updated', direction: 'asc' });
  public sortChanged$ = new Subject<ExpandListSort<ChargingUser>>();
  public defaultSort: Sort = { active: 'updated', direction: 'asc' };
  public refresh$ = new Subject<void>();
  public refreshChargingUserSubject = new Subject<ChargingUser>();
  public focusedChargingUser = signal<ChargingUser | null>(null);
  public resetOrgFilterSignal = signal<{ reset: boolean }>({ reset: false });
  public resetDepartmentFilterSignal = signal<{ reset: boolean }>({ reset: false });

  public pagingOptions: PagingOptions = {
    pageSize: this.defaultPageSize,
    pageSizeOptions: this.pageSizes,
  };

  public filters = signal<FilterState>({
    organizationId: this.queryParamMap$()?.get(ChargingUserListQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()!.get(ChargingUserListQueryParams.organizationId)!)
      : null,
    departmentId: this.queryParamMap$()?.get(ChargingUserListQueryParams.departmentId)
      ? parseInt(this.queryParamMap$()!.get(ChargingUserListQueryParams.departmentId)!)
      : null,
    query: this.queryParamMap$()?.get(ChargingUserListQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()!.get(ChargingUserListQueryParams.query)!)
      : null,
    selectedChargingUserStatus: (() => {
      const param = this.queryParamMap$()?.get(ChargingUserListQueryParams.chargingUserStatus);
      return param ? this.filteringService.safeCastEnum(ChargingUserStatus, param) : null;
    })(),
    selectedIdTagUserType: (() => {
      const param = this.queryParamMap$()?.get(ChargingUserListQueryParams.idTagUserType);
      return param ? this.filteringService.safeCastEnum(IdTagUserType, param) : null;
    })(),
    sort: this.defaultSort,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
    chargingUserId: this.queryParamMap$()?.get(ChargingUserListQueryParams.chargingUserId)
      ? parseInt(this.queryParamMap$()!.get(ChargingUserListQueryParams.chargingUserId)!)
      : null,
  });

  public chargingUsersSignal = signal<PagedResult<ChargingUser>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.defaultPageSize,
  });

  constructor() {
    super();

    // Main data loading pipeline
    combineLatest([toObservable(this.filters), this.refresh$.pipe(startWith(void 0))])
      .pipe(
        debounceTime(300),
        tap(() => this.loading.set(true)),
        switchMap(([filters]) => {
          const evt = new ChargingUserSearchEvent(filters.page);
          const searchByChargingUserId = !!filters.chargingUserId;

          if (searchByChargingUserId) {
            evt.chargingUserId = filters.chargingUserId ?? undefined;
            evt.paging = {
              ...filters.page,
              pageIndex: 0,
              pageSize: 1,
            };
          } else {
            evt.query = filters.query;
            evt.chargingUserStatuses = filters.selectedChargingUserStatus ? [filters.selectedChargingUserStatus] : [];
            evt.idTagUserType = filters.selectedIdTagUserType ?? undefined;
            evt.organizationId = filters.organizationId ?? undefined;
            evt.departmentId = filters.departmentId ?? undefined;
          }

          evt.sorting = filters.sort;

          return this.chargingUserService.getChargingUsers(evt).pipe(
            catchError(() => {
              this.loading.set(false);
              this.snackbarService.error('Der opstod en ukendt fejl ved hentning af brugere');
              return of<PagedResult<ChargingUser>>({
                items: [],
                totalCount: 0,
                pageIndex: 0,
                pageSize: this.defaultPageSize,
              });
            })
          );
        }),
        tap(result => {
          if (this.filters().chargingUserId && result.items.length === 1) {
            this.focusedChargingUser.set(result.items[0]);
          } else {
            this.focusedChargingUser.set(null);
          }

          this.chargingUsersSignal.set(result);
          this.loading.set(false);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    // Single-item refresh pipeline
    this.refreshChargingUserSubject
      .pipe(
        switchMap(chargingUser =>
          this.chargingUserService
            .getChargingUsers({
              chargingUserId: chargingUser.id,
              paging: {
                length: 0,
                pageIndex: 0,
                pageSize: 1,
              },
            })
            .pipe(
              map(result => result.items[0]),
              switchMap(freshChargingUser =>
                freshChargingUser ? of(freshChargingUser) : this.chargingUserService.getChargingUser(chargingUser.id)
              ),
              tap(freshChargingUser => {
                const cachedResult = this.chargingUsersSignal();
                const itemIndex = cachedResult.items.findIndex(s => s.id === chargingUser.id);
                if (itemIndex === -1) return;

                const highlight = new BehaviorSubject<boolean>(true);
                const newItems = [...cachedResult.items];
                newItems[itemIndex] = {
                  ...freshChargingUser,
                  highlight: highlight,
                };

                this.chargingUsersSignal.set({
                  ...cachedResult,
                  items: newItems,
                });

                setTimeout(() => highlight.next(false), 3000);
              })
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe();

    // Sort-change pipeline
    this.sortChanged$.pipe(takeUntilDestroyed()).subscribe(sort => {
      this.loading.set(true);
      this.currentSort.set(sort);

      const matSort: Sort = {
        active: sort.column as string,
        direction: sort.direction,
      };

      this.filters.update(f => ({
        ...f,
        sort: matSort,
        page: {
          length: f.page.length,
          pageIndex: 0,
          pageSize: f.page.pageSize,
        },
      }));
    });

    // Sync filters → URL (only write non-null params, replaceUrl)
    toObservable(this.filters)
      .pipe(
        map(filters => ({
          chargingUserId: filters.chargingUserId?.toString(),
          organizationId: filters.organizationId?.toString(),
          departmentId: filters.departmentId?.toString(),
          query: filters.query && filters.query.trim() ? encodeURIComponent(filters.query) : undefined,
          chargingUserStatus: filters.selectedChargingUserStatus?.toString(),
          idTagUserType: filters.selectedIdTagUserType?.toString(),
        })),
        distinctUntilChanged(
          (previous, current) =>
            previous.chargingUserId === current.chargingUserId &&
            previous.organizationId === current.organizationId &&
            previous.departmentId === current.departmentId &&
            previous.query === current.query &&
            previous.chargingUserStatus === current.chargingUserStatus &&
            previous.idTagUserType === current.idTagUserType
        ),
        takeUntilDestroyed()
      )
      .subscribe(filters => {
        const queryParams: Record<string, string | undefined> = {};

        if (filters.chargingUserId) {
          queryParams[ChargingUserListQueryParams.chargingUserId] = filters.chargingUserId;
        }
        if (filters.organizationId) {
          queryParams[ChargingUserListQueryParams.organizationId] = filters.organizationId;
        }
        if (filters.departmentId) {
          queryParams[ChargingUserListQueryParams.departmentId] = filters.departmentId;
        }
        if (filters.query) {
          queryParams[ChargingUserListQueryParams.query] = filters.query;
        }
        if (filters.chargingUserStatus) {
          queryParams[ChargingUserListQueryParams.chargingUserStatus] = filters.chargingUserStatus;
        }
        if (filters.idTagUserType) {
          queryParams[ChargingUserListQueryParams.idTagUserType] = filters.idTagUserType;
        }

        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams,
          replaceUrl: true,
        });
      });

    // Watch for external URL changes (e.g. from search overlay) and sync back to filters
    toObservable(this.queryParamMap$)
      .pipe(
        map(queryParamMap => {
          if (!queryParamMap) {
            return null;
          }

          const chargingUserIdParam = queryParamMap.get(ChargingUserListQueryParams.chargingUserId);
          return chargingUserIdParam ? parseInt(chargingUserIdParam) : null;
        }),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(urlChargingUserId => {
        if (urlChargingUserId === null) {
          return;
        }

        const currentFilters = this.filters();
        if (currentFilters.chargingUserId !== urlChargingUserId) {
          this.filters.update(f => ({
            ...f,
            chargingUserId: urlChargingUserId,
          }));
        }
      });
  }

  public refreshChargingUser(chargingUser: ChargingUser) {
    this.refreshChargingUserSubject.next(chargingUser);
  }

  public refreshList() {
    this.refresh$.next();
  }

  public onSortChange(sort: ExpandListSort<ChargingUser>) {
    this.sortChanged$.next(sort);
  }

  public resetFilter() {
    this.focusedChargingUser.set(null);

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true,
    });

    this.resetOrgFilterSignal.set({ reset: true });
    this.resetDepartmentFilterSignal.set({ reset: true });

    this.filters.set({
      organizationId: null,
      sort: this.defaultSort,
      departmentId: null,
      query: null,
      selectedChargingUserStatus: null,
      selectedIdTagUserType: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
      chargingUserId: null,
    });
  }
}
